import { canRescheduleBlock, type RescheduleBlockCheck } from '@cadencia/domain';
import type { TrainingBlock } from '@cadencia/contracts';
import { db } from '../db.js';
import { addDays, daysBetween, startOfWeek, todayLocal } from '../../lib/dates.js';
import { listSessionsOfWeek, listWeeks } from '../repositories/trainingRepo.js';
import { parchear } from '../parchear.js';
import { putRow, type WriteContext } from '../writes.js';
import {
  trainingBlockSchema,
  trainingSessionSchema,
  trainingWeekSchema,
  cardioPrescriptionSchema,
} from '@cadencia/contracts';

/**
 * Cambiar el día en el que empieza un bloque.
 *
 * La fecha se elige al CREAR el bloque, y esa tarjeta solo aparece cuando no
 * hay ninguno activo. Un bloque creado para probar la aplicación se queda ahí
 * corriendo solo: pasan las semanas y la cabecera dice «semana 3 de 4» sin que
 * se haya entrenado nada, sin forma de volver atrás.
 *
 * Se mueve el bloque ENTERO por el mismo desplazamiento: sus semanas, las
 * sesiones ya materializadas y el cardio prescrito. Mover solo el bloque
 * dejaría las semanas donde estaban, que es un estado que nada sabe leer.
 *
 * Si se puede o no lo decide `canRescheduleBlock` en el dominio: la regla es
 * que no se falsea el historial —ni moviendo trabajo ya registrado, ni
 * fabricando sesiones perdidas hacia atrás—.
 */

export interface RescheduleOutcome extends RescheduleBlockCheck {
  moved: boolean;
  /** El lunes al que ha quedado, si se movió. */
  startedOn: string | null;
}

/**
 * ¿Hay entrenamiento registrado dentro del bloque?
 *
 * Una sesión PERDIDA no cuenta: no se entrenó nada, y es justo el estado en el
 * que queda un bloque de pruebas abandonado. Lo que cuenta es haber empezado.
 */
export async function blockHasLoggedWork(userId: string, blockId: string): Promise<boolean> {
  const sesiones = (await db.trainingSessions.where('blockId').equals(blockId).toArray()).filter(
    (fila) => fila.deletedAt === null,
  );
  if (sesiones.some((fila) => fila.status === 'completed' || fila.status === 'in_progress')) {
    return true;
  }

  // Y por si una sesión quedó `planned` con su registro a medias: lo que manda
  // es que exista el parte de entrenamiento, no el estado de la sesión.
  const suyas = new Set(sesiones.map((fila) => fila.id));
  const logs = await db.workoutLogs.where('userId').equals(userId).toArray();
  return logs.some(
    (log) => log.deletedAt === null && log.sessionId !== null && suyas.has(log.sessionId),
  );
}

export async function rescheduleBlock(
  ctx: WriteContext,
  block: TrainingBlock,
  nuevaFecha: string,
  today = todayLocal(),
): Promise<RescheduleOutcome> {
  // Igual que al crearlo: la fecha elegida no se usa tal cual, se lleva al
  // lunes de su semana. El plan cuenta por semanas de lunes a domingo.
  const nuevoInicio = startOfWeek(nuevaFecha);

  const check = canRescheduleBlock({
    currentStart: block.startedOn,
    newStart: nuevoInicio,
    today,
    hasLoggedWork: await blockHasLoggedWork(ctx.userId, block.id),
  });
  if (!check.allowed) return { ...check, moved: false, startedOn: null };

  const desplazamiento = daysBetween(block.startedOn, nuevoInicio);

  const semanas = await listWeeks(block.id);
  for (const semana of semanas) {
    const nuevoLunes = addDays(semana.weekStart, desplazamiento);

    for (const sesion of await listSessionsOfWeek(semana.id)) {
      await putRow(
        ctx,
        'trainingSessions',
        parchear(
          trainingSessionSchema,
          sesion,
          { scheduledDate: addDays(sesion.scheduledDate, desplazamiento) },
          { tabla: 'training_sessions', id: sesion.id },
        ),
      );
    }

    // El cardio va por fecha, no por semana: se busca por el rango que ocupaba.
    const cardio = (
      await db.cardioPrescriptions.where('userId').equals(ctx.userId).toArray()
    ).filter(
      (fila) =>
        fila.deletedAt === null &&
        fila.scheduledDate >= semana.weekStart &&
        fila.scheduledDate < addDays(semana.weekStart, 7),
    );
    for (const fila of cardio) {
      await putRow(
        ctx,
        'cardioPrescriptions',
        parchear(
          cardioPrescriptionSchema,
          fila,
          { scheduledDate: addDays(fila.scheduledDate, desplazamiento) },
          { tabla: 'cardio_prescriptions', id: fila.id },
        ),
      );
    }

    await putRow(
      ctx,
      'trainingWeeks',
      parchear(trainingWeekSchema, semana, { weekStart: nuevoLunes }, {
        tabla: 'training_weeks',
        id: semana.id,
      }),
    );
  }

  await putRow(
    ctx,
    'trainingBlocks',
    parchear(trainingBlockSchema, block, { startedOn: nuevoInicio }, {
      tabla: 'training_blocks',
      id: block.id,
    }),
  );

  return { ...check, moved: true, startedOn: nuevoInicio };
}
